import { getAccessToken } from '@dental-staffing-platform/auth';
import {
  taxDocument,
  taxDocumentDetail,
  taxDocumentPage,
  taxRecipientPage,
  taxRequest,
  taxRequestDetail,
  taxRequestPage,
  type CreateTaxDocumentInput,
  type TaxDocumentActionInput,
  type TaxRequestActionInput,
} from '@dental-staffing-platform/shared';
import { req } from './admin-client.js';
function query(path: string, params: Record<string, string | number | undefined>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  return `${path}?${q}`;
}
export const taxAdminClient = {
  documents: async (params: Record<string, string | number | undefined>) =>
    taxDocumentPage.parse(await req(query('/api/admin/tax-documents', params))),
  document: async (id: string, page = 1) =>
    taxDocumentDetail.parse(
      await req(query(`/api/admin/tax-documents/${encodeURIComponent(id)}`, { page, pageSize: 20 }))
    ),
  recipients: async (params: Record<string, string | number | undefined>) =>
    taxRecipientPage.parse(await req(query('/api/admin/tax-documents/recipients', params))),
  create: async (input: CreateTaxDocumentInput) =>
    taxDocument.parse(
      await req('/api/admin/tax-documents', { method: 'POST', body: JSON.stringify(input) })
    ),
  edit: async (
    id: string,
    input: { clientRequestId: string; expectedRevision: number; title: string; issuer: string }
  ) =>
    taxDocument.parse(
      await req(`/api/admin/tax-documents/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      })
    ),
  action: async (id: string, input: TaxDocumentActionInput) =>
    taxDocument.parse(
      await req(`/api/admin/tax-documents/${encodeURIComponent(id)}/actions`, {
        method: 'POST',
        body: JSON.stringify(input),
      })
    ),
  upload: async (
    id: string,
    input: { clientRequestId: string; expectedRevision: number; pdfBase64: string }
  ) =>
    taxDocument.parse(
      await req(`/api/admin/tax-documents/${encodeURIComponent(id)}/file`, {
        method: 'PUT',
        body: JSON.stringify(input),
      })
    ),
  requests: async (params: Record<string, string | number | undefined>) =>
    taxRequestPage.parse(await req(query('/api/admin/tax-documents/requests', params))),
  request: async (id: string, page = 1) =>
    taxRequestDetail.parse(
      await req(
        query(`/api/admin/tax-documents/requests/${encodeURIComponent(id)}`, { page, pageSize: 20 })
      )
    ),
  requestAction: async (id: string, input: TaxRequestActionInput) =>
    taxRequest.parse(
      await req(`/api/admin/tax-documents/requests/${encodeURIComponent(id)}/events`, {
        method: 'POST',
        body: JSON.stringify(input),
      })
    ),
  download: async (id: string) => {
    const token = getAccessToken();
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/admin/tax-documents/${encodeURIComponent(id)}/file`,
      { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
    );
    if (!response.ok || !response.headers.get('Content-Type')?.startsWith('application/pdf'))
      throw new Error('The private PDF could not be downloaded. Refresh and try again.');
    const file = await response.blob();
    if (!file.size || file.size > 3 * 1024 * 1024)
      throw new Error('The document download was invalid.');
    const url = URL.createObjectURL(file),
      anchor = document.createElement('a');
    anchor.href = url;
    anchor.download =
      /filename="([A-Za-z0-9_.-]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ??
      'tax-document.pdf';
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
export async function taxPdfData(file: File) {
  if (file.type !== 'application/pdf' || file.size > 3 * 1024 * 1024)
    throw new Error('Choose a PDF up to 3 MB.');
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const sha = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
  return { pdfBase64: btoa(binary), sha };
}
